import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RefreshCw, CalendarClock, Radio, CheckCircle2, Zap, Inbox, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import JourneyCard from "@/components/JourneyCard";
import JourneyDetail from "@/components/JourneyDetail";
import { statusMeta, fmtTime } from "@/lib/status";
import api from "@/lib/api";

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="border border-zinc-800 bg-zinc-900/60 rounded-md p-4">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <div className="text-3xl font-heading font-extrabold mt-2 tracking-tight" style={{ color }}>
        {value ?? "—"}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);

  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.getSettings, refetchInterval: 8000 });
  const { data: journeys = [], isLoading } = useQuery({
    queryKey: ["journeys"], queryFn: api.listJourneys, refetchInterval: 5000,
  });
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: api.getStats, refetchInterval: 5000 });
  const { data: logs = [] } = useQuery({ queryKey: ["logs"], queryFn: () => api.getLogs(20), refetchInterval: 5000 });

  const selectedLive = selected ? journeys.find((x) => x.journeyId === selected.journeyId) || selected : null;

  const syncMut = useMutation({
    mutationFn: api.syncNow,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["journeys"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      if (d.count === 0) {
        toast.message("0 rezervasyon bulundu — şirketinize atanmış (kabul edilmiş) iş yok. Yeni oluşturulan rezervasyon size atanana kadar görünmez.");
      } else {
        toast.success(`${d.count} rezervasyon senkronize edildi`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Senkronizasyon başarısız"),
  });

  const cleanupMut = useMutation({
    mutationFn: api.cleanupJourneys,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["journeys"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success(`${d.deleted} eski rezervasyon temizlendi`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Temizlenemedi"),
  });

  const masterMut = useMutation({
    mutationFn: (v) => api.updateSettings({ automationEnabled: v }),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast[d.automationEnabled ? "success" : "message"](
        d.automationEnabled ? "Otomasyon açıldı" : "Otomasyon kapatıldı"
      );
    },
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, enabled }) => api.toggleAutomation(id, enabled),
    onMutate: async ({ id, enabled }) => {
      await qc.cancelQueries({ queryKey: ["journeys"] });
      const prev = qc.getQueryData(["journeys"]);
      qc.setQueryData(["journeys"], (old = []) =>
        old.map((x) => (x.journeyId === id ? { ...x, automationEnabled: enabled } : x))
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(["journeys"], ctx.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: ["journeys"] }),
  });

  const active = journeys.filter((j) => !["COMPLETED", "CANCELLED_FREE", "CANCELLED_WITH_COSTS"].includes(j.status));
  const finished = journeys.filter((j) => ["COMPLETED", "CANCELLED_FREE", "CANCELLED_WITH_COSTS"].includes(j.status));

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Kontrol Paneli</h1>
          <p className="text-sm text-zinc-500 mt-1">Rezervasyonları otomatik olarak yönetin</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2.5 border border-zinc-800 bg-zinc-900/60 rounded-md px-3 py-2">
            <Zap className={`h-4 w-4 ${settings?.automationEnabled ? "text-blue-400" : "text-zinc-500"}`} />
            <span className="text-sm text-zinc-300">Otomasyon</span>
            <Switch
              data-testid="master-automation-switch"
              checked={!!settings?.automationEnabled}
              onCheckedChange={(v) => masterMut.mutate(v)}
            />
          </div>
          <Button
            data-testid="sync-now-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white active:scale-95"
            onClick={() => syncMut.mutate()}
            disabled={syncMut.isPending}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncMut.isPending ? "animate-spin" : ""}`} />
            Şimdi Senkronize Et
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat icon={CalendarClock} label="Bekleyen" value={stats?.upcoming} color="#a1a1aa" />
        <Stat icon={Radio} label="Aktif" value={stats?.active} color="#8b5cf6" />
        <Stat icon={CheckCircle2} label="Bugün Tamamlanan" value={stats?.completedToday} color="#10b981" />
        <Stat icon={Zap} label="Otomatik" value={stats?.automated} color="#2563eb" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Journeys */}
        <div className="xl:col-span-2">
          {isLoading ? (
            <div className="text-zinc-500 font-mono text-sm p-6">Yükleniyor…</div>
          ) : journeys.length === 0 ? (
            <div className="border border-dashed border-zinc-800 rounded-md p-10 text-center">
              <Inbox className="h-8 w-8 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">
                {settings?.apiKeySet
                  ? "Rezervasyon bulunamadı. Senkronize etmeyi deneyin."
                  : "API bağlantısı yok. Başlamak için ayarlardan API anahtarınızı girin."}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
                  Aktif Rezervasyonlar ({active.length})
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {active.map((j) => (
                    <JourneyCard
                      key={j.journeyId}
                      journey={j}
                      onOpen={setSelected}
                      onToggle={(jj, v) => toggleMut.mutate({ id: jj.journeyId, enabled: v })}
                    />
                  ))}
                </div>
              </div>
              {finished.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      Tamamlanan ({finished.length})
                    </div>
                    <Button
                      data-testid="cleanup-btn"
                      variant="outline" size="sm"
                      className="h-7 border-zinc-700 bg-transparent hover:bg-red-950/40 hover:border-red-800 hover:text-red-300 text-zinc-400"
                      onClick={() => cleanupMut.mutate()}
                      disabled={cleanupMut.isPending}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Eskileri Temizle
                    </Button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 opacity-70">
                    {finished.map((j) => (
                      <JourneyCard
                        key={j.journeyId}
                        journey={j}
                        onOpen={setSelected}
                        onToggle={(jj, v) => toggleMut.mutate({ id: jj.journeyId, enabled: v })}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Activity feed */}
        <div className="xl:col-span-1">
          <div className="border border-zinc-800 bg-zinc-900/60 rounded-md">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
              <Radio className="h-4 w-4 text-blue-400" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Canlı Aktivite</span>
            </div>
            <div className="p-2 max-h-[560px] overflow-y-auto font-mono text-xs">
              {logs.length === 0 ? (
                <div className="text-zinc-600 p-4">Henüz aktivite yok.</div>
              ) : (
                logs.map((l, i) => (
                  <div key={i} className="px-2 py-1.5 hover:bg-zinc-800/50 rounded flex gap-2">
                    <span className="text-zinc-600 shrink-0">{fmtTime(l.timestamp)}</span>
                    <span
                      className={
                        l.level === "success" ? "text-emerald-400"
                          : l.level === "error" ? "text-red-400" : "text-zinc-400"
                      }
                    >
                      {l.message}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <JourneyDetail journey={selectedLive} open={!!selected} onClose={() => setSelected(null)} />
    </div>
  );
}
